import AnimationContainer from "components/animation-container";
import BaffleText from "components/baffle-text";
import React from "react";
import styled, { keyframes } from "styled-components";

const Blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const TerminalContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  color: #04e5e5;
  text-transform: none;
  font-weight: 500;
  text-align: left;
  line-height: 1.6;
  z-index: 10;
  pointer-events: none;
  @media (max-width: 500px) {
    padding: 20px;
    font-size: 12px;
  }
`;

const LogLine = styled.div`
  margin: 2px 0;
  opacity: 0.9;
  text-shadow: 0 0 5px rgba(4, 229, 229, 0.4);
  &.cursor {
    animation: ${Blink} 1s infinite;
    display: inline-block;
    color: #04e5e5;
  }
`;

const BOOT_LOGS = [
  { text: "root@kali:~# nmap -p 22,80 192.168.1.100", delay: 100 },
  { text: "Discovered open ports. Target OS: Linux.", delay: 120 },
  { text: "root@kali:~# msfconsole -q -x 'use exploit/linux/http/apache_mod_cgi_bash_env_exec; exploit'", delay: 180 },
  { text: "[*] Sending stage (2991206 bytes) to 192.168.1.100", delay: 120 },
  { text: "[-] Exploit aborted due to failure: not-vulnerable", color: "#ff4444", delay: 200 },
  { text: "[!] WAF detected. Connection dropped by host.", color: "#ff4444", delay: 150 },
  { text: "root@kali:~# set EvasionOptions Encoders=x86/shikata_ga_nai", delay: 100 },
  { text: "root@kali:~# exploit -j -z", delay: 120 },
  { text: "Bypassing WAF rulesets... Success.", delay: 150 },
  { text: "[+] Meterpreter session 1 opened", color: "#00ff00", delay: 120 },
  { text: "meterpreter > getsystem", delay: 120 },
  { text: "...got system via technique 1.", delay: 100 },
  { text: "[+] Root privileges acquired.", color: "#00ff00", delay: 250 }
];


const RevealContainer = styled.div`
      position: fixed;
      z-index: 20000;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
      background-color: #000;
      opacity: 1;
      visibility: visible;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      font-size: 50px;
      color: #fff;
      text-transform: uppercase;
      font-weight: 700;
      @media (max-width: 500px) {
        font-size: 30px;
      }
    `;

const RevealAnimation = keyframes`
        0% {
            transform: translate3d(100%, 0, 0);
        }
        35%, 65% {
            transform: translate3d(0, 0, 0);
        }
        100% {
            transform: translate3d(-100%, 0, 0);
        }
    `;

const Reveal = styled.div`
      position: fixed;
      width: 100%;
      pointer-events: none;
      height: 100%;
      background-color: #04e5e5;
      transform: translateX(100%);
      &.animate {
        animation: ${RevealAnimation} 1.1s cubic-bezier(0.2, 1, 0.3, 1) forwards;
      }
    `;

class PageRevealer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      animation: false,
      complete: false,
      hide: false,
      isTransparent: false,
      bootLogs: [],
      bootComplete: false,
    };
    this.timer1 = null;
    this.timer2 = null;
    this.bootTimer = null;
  }

  componentDidMount() {
    this.processBootSequence(0);
  }

  processBootSequence = (index) => {
    if (index < BOOT_LOGS.length) {
      const log = BOOT_LOGS[index];
      this.bootTimer = setTimeout(() => {
        this.setState(prevState => ({ 
          bootLogs: [...prevState.bootLogs, log] 
        }));
        this.processBootSequence(index + 1);
      }, log.delay);
    } else {
      this.bootTimer = setTimeout(() => {
        this.setState({ bootComplete: true });
      }, 400);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer1);
    clearTimeout(this.timer2);
    clearTimeout(this.bootTimer);
  }

  reveal = () => { // 使用箭头函数自动绑定 this
    if (!this.state.complete) {
      this.setState({ animation: true, complete: true });
      
      this.timer1 = setTimeout(() => {
        this.setState({ isTransparent: true }); // 修改状态而非操作 DOM
        
        this.timer2 = setTimeout(() => {
          this.setState({ animation: false, hide: true });
          // Signal all viewport-gated animations that the page is now visible
          if (typeof window !== 'undefined') {
            window.__pageRevealed = true;
            window.dispatchEvent(new Event('pageRevealed'));
          }
        }, 500);
      }, 400);
    }
  }

  renderBootSequence() {
    if (this.state.complete || this.state.bootComplete) return null;
    return (
      <TerminalContainer>
        {this.state.bootLogs.map((log, i) => (
          <LogLine key={i} style={{ color: log.color || "#04e5e5" }}>{log.text}</LogLine>
        ))}
        {!this.state.bootComplete && <LogLine className="cursor">▌</LogLine>}
      </TerminalContainer>
    );
  }

  renderBaffle() {
    if (this.state.complete || !this.state.bootComplete) return null;

    return (
      <AnimationContainer animation="fadeIn" force={true} style={{ zIndex: 20 }}>
        <BaffleText
          text="Welcome"
          revealDuration={500}
          revealDelay={300}
          parentMethod={this.reveal}
          callMethodTime={1000}
          force={true}
        />
      </AnimationContainer>
    );
  }

  render() {
    const { hide, isTransparent, animation } = this.state;
    
    return (
      <RevealContainer
        id="reveal_container"
        style={{ 
          display: hide ? "none" : "flex",
          backgroundColor: isTransparent ? "transparent" : "#000" // 声明式样式
        }}
      >
        {this.renderBootSequence()}
        {this.renderBaffle()}
        <Reveal
          id="revealer"
          className={animation ? "animate" : ""}
        />
      </RevealContainer>
    );
  }
}

export default PageRevealer;
